import React, { useState, useEffect } from 'react';
import { Calendar, Scissors, Star, CheckCircle, ShieldCheck, Sparkles, Coffee, User, Gift, Award, ArrowRight, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { services } from '../data/services';
import GoldenSparksCanvas from './GoldenSparksCanvas';

export default function Hero({ onOpenBooking, onOpenAuth, onOpenProfile, onNavigate }) {
  const { lang, t } = useLanguage();
  const { currentUser, loyaltyInfo } = useAuth();
  const hT = t.hero;

  const [activeSlide, setActiveSlide] = useState(0);

  const showcaseSlides = [
    {
      id: "hp-full-package",
      serviceId: "hp-full-package",
      tabLabel: lang === 'nl' ? "👑 Compleet Pakket" : lang === 'tr' ? "👑 Tam Paket" : "👑 Full Package",
      image: "/images/hair_system_result_3_1788255394814.webp",
      imageMob: "/images/hair_system_result_3_1788255394814_mob.webp",
      badge: lang === 'nl' ? "100% NATUURLIJK ECHT HAAR" : lang === 'tr' ? "%100 DOĞAL GERÇEK SAÇ" : "100% REAL HUMAN HAIR",
      badgeColor: "bg-amber-400 text-slate-950 font-black",
      title: lang === 'nl' ? "COMPLEET HEREN HAARWERK PAKKET" : lang === 'tr' ? "TAM ERKEK PROTEZ SAÇ PAKETİ" : "FULL MEN'S HAIR SYSTEM PACKAGE",
      desc: lang === 'nl' ? "100% echt haarwerk, maatwerk plaatsing, medische verlijming en naadloos knippen." : lang === 'tr' ? "%100 gerçek doğal saç protezi, kişiye özel kalıp, medikal sabitleme ve sıfır geçişli kesim." : "100% real human hair, custom base fitting, medical bonding and seamless cut.",
      price: "€500,00",
      priceColor: "bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950",
      duration: "120 min"
    },
    {
      id: "hp-consultation",
      serviceId: "hp-consultation",
      tabLabel: lang === 'nl' ? "✨ Gratis Intake" : lang === 'tr' ? "✨ Ücretsiz Görüşme" : "✨ Free Intake",
      image: "/images/hair_system_result_1_1788255360381.webp",
      imageMob: "/images/hair_system_result_1_1788255360381_mob.webp",
      badge: lang === 'nl' ? "100% VRIJBLIJVEND & PERSOONLIJK" : lang === 'tr' ? "%100 ÜCRETSİZ & BAĞLAYICILIĞI YOKTUR" : "100% FREE & CONFIDENTIAL",
      badgeColor: "bg-emerald-400 text-slate-950 font-black",
      title: lang === 'nl' ? "ADVIESGESPREK & OPMETEN HAARWERK" : lang === 'tr' ? "ÖLÇÜM VEYA ÖN GÖRÜŞME" : "MEASUREMENT & CONSULTATION",
      desc: lang === 'nl' ? "100% gratis en vrijblijvende intake, mal opmeten en haarstructuuranalyse." : lang === 'tr' ? "Ücretsiz ön görüşme, kafa yapısına özel kalıp çıkarma ve saç analizi." : "100% free intake, scalp mold measuring and hair texture analysis.",
      price: lang === 'nl' ? "GRATIS" : lang === 'tr' ? "ÜCRETSİZ" : "FREE",
      priceColor: "bg-emerald-500 text-slate-950",
      duration: "30 min"
    },
    {
      id: "hp-monthly-maintenance",
      serviceId: "hp-monthly-maintenance",
      tabLabel: lang === 'nl' ? "🛡️ Onderhoud" : lang === 'tr' ? "🛡️ Aylık Bakım" : "🛡️ Maintenance",
      image: "/images/hair_system_result_4_1788255415949.webp",
      imageMob: "/images/hair_system_result_4_1788255415949_mob.webp",
      badge: lang === 'nl' ? "DIEPTEREINIGING & VERNIEUWING" : lang === 'tr' ? "DERİN TEMİZLİK & YENİLEME" : "DETOX WASH & RE-TAPING",
      badgeColor: "bg-cyan-400 text-slate-950 font-black",
      title: lang === 'nl' ? "MAANDELIJKS ONDERHOUD & STYLING" : lang === 'tr' ? "AYLIK BAKIM, ŞEKİLLENDİRME & KESİM" : "MONTHLY CARE & STYLING",
      desc: lang === 'nl' ? "Losmaken, dieptereiniging, hoofdhuidverzorging, eigen haar knippen, nieuwe tapes en herplaatsing." : lang === 'tr' ? "Söküm, derin temizlik, saç derisi bakımı, yan saçların kesimi, yeni bant ve yeniden uygulama." : "Removal, detox washing, scalp therapy, own hair fade cut, fresh tapes and repositioning.",
      price: "€60,00",
      priceColor: "bg-cyan-400 text-slate-950",
      duration: "60 min"
    }
  ];

  useEffect(() => {
    let timer;
    const initialDelay = setTimeout(() => {
      setActiveSlide((prev) => (prev + 1) % showcaseSlides.length);
      timer = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % showcaseSlides.length);
      }, 6000);
    }, 8000);
    return () => {
      clearTimeout(initialDelay);
      if (timer) clearInterval(timer);
    };
  }, [showcaseSlides.length]);

  const nextSlide = () => setActiveSlide((prev) => (prev + 1) % showcaseSlides.length);
  const prevSlide = () => setActiveSlide((prev) => (prev - 1 + showcaseSlides.length) % showcaseSlides.length);

  const handleSelectSlideService = (serviceId) => {
    const srv = services.find(s => s.id === serviceId);
    if (onOpenBooking) {
      onOpenBooking(srv || null);
    }
  };

  return (
    <section className="relative w-full max-w-full overflow-hidden bg-slate-950 text-white pt-20 sm:pt-22 pb-6 sm:pb-8 border-b border-slate-800 select-none">
      
      {/* Background Subtle Ambient Glow & Animated Golden Sparks */}
      <div className="absolute top-5 left-10 w-72 h-72 rounded-full glow-aurora-amber pointer-events-none opacity-20" />
      <div className="absolute bottom-5 right-10 w-72 h-72 rounded-full glow-aurora-emerald pointer-events-none opacity-15" />
      <GoldenSparksCanvas className="absolute inset-0 pointer-events-none z-0 opacity-60" />

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌓 2-COLUMN SPLIT: COMPACT BALANCED LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          
          {/* 👈 LEFT COLUMN: COMPACT TEXT & ACTIONS (7 Cols) */}
          <div className="lg:col-span-7 text-center lg:text-left space-y-3 sm:space-y-3.5">
            
            {/* Top Brand Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-amber-400/40 text-amber-300 text-[11px] font-black uppercase tracking-wider shadow-xs">
              <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
              <span>{hT.badge || "AURA HAIR ROTTERDAM"}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>

            {/* Compact Headline */}
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-[38px] font-black text-white tracking-tight leading-[1.15] uppercase">
              <span>{lang === 'nl' ? 'ONTDEK JE STIJL &' : lang === 'tr' ? 'TARZINI &' : 'DISCOVER YOUR STYLE &'} </span>
              <span className="text-amber-400 font-serif block sm:inline">
                {lang === 'nl' ? 'NIEUWE HAARLOOK' : lang === 'tr' ? 'SAÇINI KEŞFET' : 'SIGNATURE HAIR'}
              </span>
            </h1>

            {/* Compact Subtitle */}
            <p className="text-xs sm:text-[13px] text-slate-300 font-medium leading-relaxed max-w-lg mx-auto lg:mx-0">
              {lang === 'nl'
                ? "Moderne herenkapsels, skin fades, baardverzorging én niet-chirurgische haarwerken van 100% echt haar aan het Samuel Esmeijerplein 3 in Rotterdam."
                : lang === 'tr'
                ? "Rotterdam Samuel Esmeijerplein 3'te modern saç kesimi, skin fade, sakal tasarımı ve %100 gerçek insan saçından kişiye özel medikal protez saç uygulamaları."
                : "Precision haircuts, skin fades, beard sculpting and 100% real human hair systems in Rotterdam."}
            </p>

            {/* Action Buttons (Compact & Sleek) */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5 pt-0.5">
              <button
                onClick={onOpenBooking}
                className="btn-gold-sheen px-5 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-black text-xs uppercase tracking-wider shadow-md shadow-amber-500/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-1.5 border border-amber-400/40 cursor-pointer whitespace-nowrap"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>{hT.bookAppointment || (lang === 'nl' ? 'Direct Afspraak Maken' : lang === 'tr' ? 'Hemen Randevu Al' : 'Book Appointment')}</span>
              </button>

              <button
                type="button"
                onClick={() => onNavigate ? onNavigate('services') : (window.location.hash = '#hizmetler')}
                className="px-4 py-2.5 sm:py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white border border-slate-700 hover:border-amber-400 font-extrabold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-xs whitespace-nowrap cursor-pointer"
              >
                <Eye className="w-3.5 h-3.5 text-amber-400" />
                <span>{lang === 'nl' ? 'Voor & Na' : lang === 'tr' ? 'Öncesi / Sonrası' : 'Before & After'}</span>
              </button>

              <button
                type="button"
                onClick={() => onNavigate ? onNavigate('services') : (window.location.hash = '#hizmetler')}
                className="px-4 py-2.5 sm:py-3 rounded-xl bg-slate-900/60 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-600 font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <Scissors className="w-3.5 h-3.5 text-amber-400" />
                <span>{hT.viewServices || (lang === 'nl' ? 'Prijslijst' : lang === 'tr' ? 'Fiyatlarımız' : 'Services & Prices')}</span>
              </button>
            </div>

            {/* 👑 Compact Customer Loyalty Bar */}
            <div className="p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 text-left max-w-lg">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white shadow-xs shrink-0">
                  {currentUser ? <Award className="w-3.5 h-3.5" /> : <Gift className="w-3.5 h-3.5" />}
                </div>
                <div>
                  {currentUser ? (
                    <div className="flex items-center gap-1.5">
                      <div className="text-[11px] font-bold text-white">
                        {lang === 'nl' ? `Welkom, ${currentUser.name}` : lang === 'tr' ? `Hoş Geldiniz, ${currentUser.name}` : `Welcome, ${currentUser.name}`}
                      </div>
                      <span className="px-1 py-0.2 rounded bg-amber-500 text-white font-mono font-bold text-[9px]">
                        {loyaltyInfo.stamps}/10 ✂️
                      </span>
                    </div>
                  ) : (
                    <div>
                      <div className="text-[11px] font-bold text-white">
                        {lang === 'nl' ? 'Gratis Knipbeurt Spaarkaart' : lang === 'tr' ? '10 Randevuda 1 Bedava Saç Kesimi' : 'Free Haircut Loyalty'}
                      </div>
                      <p className="text-[10px] text-slate-300">
                        {lang === 'nl' ? 'Exclusief voor geregistreerde klanten: elke 10 afspraken = 1 gratis knipbeurt.' : lang === 'tr' ? 'Sadece kayıtlı müşterilere özel: Her 10 randevuda 1 bedava saç kesimi!' : 'Exclusive for registered clients: 1 free haircut every 10 bookings.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full sm:w-auto shrink-0">
                {currentUser ? (
                  <button
                    onClick={onOpenProfile}
                    className="w-full sm:w-auto px-3 py-2 min-h-[44px] rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                  >
                    <User className="w-3.5 h-3.5" />
                    <span>{t.profile.title}</span>
                  </button>
                ) : (
                  <button
                    onClick={onOpenAuth}
                    className="w-full sm:w-auto px-3 py-2 min-h-[44px] rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 font-extrabold text-xs flex items-center justify-center gap-1.5 border border-amber-400/30 transition-all cursor-pointer"
                  >
                    <User className="w-3.5 h-3.5 text-amber-400" />
                    <span>{t.auth.loginBtn}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Compact 4 Micro Highlights */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 pt-0.5 max-w-lg">
              <div className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center gap-1.5">
                <CheckCircle className="w-3 h-3 text-amber-400 shrink-0" />
                <span className="text-[10px] font-bold text-slate-200 truncate">{hT.noWaiting || "Sıfır Bekleme"}</span>
              </div>
              <div className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center gap-1.5">
                <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />
                <span className="text-[10px] font-bold text-slate-200 truncate">5.0 ★ Google</span>
              </div>
              <div className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-emerald-400 shrink-0" />
                <span className="text-[10px] font-bold text-slate-200 truncate">{t.hairpiece?.humanHairFeature || "%100 Gerçek Saç"}</span>
              </div>
              <div className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center gap-1.5">
                <Coffee className="w-3 h-3 text-rose-400 shrink-0" />
                <span className="text-[10px] font-bold text-slate-200 truncate">{hT.freeDrinks || "İkram Barı"}</span>
              </div>
            </div>

          </div>

          {/* 👉 RIGHT COLUMN: LUXURY HAARWERK HEREN SHOWCASE AD PANEL (5 Cols) */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none">
              
              {/* Subtle Frame Glow Accent */}
              <div className="absolute -inset-1.5 rounded-3xl bg-gradient-to-tr from-amber-500 via-rose-500 to-amber-400 opacity-25 blur-md pointer-events-none animate-pulse" />
              
              {/* Showcase Billboard Card */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-amber-400/40 bg-slate-900 shadow-2xl flex flex-col group">
                
                {/* 🏷️ Top Header Bar: Reklam / Vitrin Başlığı */}
                <div className="px-3.5 py-2 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 border-b border-amber-400/30 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span className="text-[11px] font-black uppercase tracking-wider text-amber-400 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-amber-400" />
                      {lang === 'nl' ? 'HAARWERK HEREN' : lang === 'tr' ? 'ERKEK PROTEZ SAÇ' : 'MEN\'S HAIR SYSTEMS'}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-[10px] font-bold">
                    Rotterdam
                  </span>
                </div>

                {/* 🎛️ 3-Tab Interactive Quick Switcher */}
                <div className="grid grid-cols-3 gap-1 p-1.5 bg-slate-950/80 border-b border-slate-800">
                  {showcaseSlides.map((slide, idx) => (
                    <button
                      key={slide.id}
                      onClick={() => setActiveSlide(idx)}
                      className={`min-h-[44px] py-2 px-1 rounded-lg text-[10px] sm:text-[11px] font-black transition-all cursor-pointer truncate text-center flex items-center justify-center ${
                        activeSlide === idx
                          ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 shadow-md shadow-amber-500/20 scale-[1.02]'
                          : 'bg-slate-900/60 text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      {slide.tabLabel}
                    </button>
                  ))}
                </div>

                {/* 🖼️ Image Slider Box */}
                <div className="relative h-[255px] sm:h-[280px] md:h-[305px] lg:h-[320px] overflow-hidden bg-slate-950">
                  {showcaseSlides.map((slide, idx) => (
                    <div
                      key={slide.id}
                      className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                        activeSlide === idx ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                      }`}
                    >
                      <picture>
                        <source media="(max-width: 640px)" srcSet={slide.imageMob || slide.image} type="image/webp" />
                        <source srcSet={slide.image} type="image/webp" />
                        <img
                          src={slide.imageMob || slide.image}
                          alt={slide.title}
                          width="600"
                          height="400"
                          className="w-full h-full object-cover object-top filter brightness-[0.9] contrast-[1.05]"
                          loading={idx === 0 ? "eager" : "lazy"}
                          fetchPriority={idx === 0 ? "high" : "low"}
                          decoding={idx === 0 ? "sync" : "async"}
                        />
                      </picture>

                      {/* Top Floating Badge */}
                      <div className="absolute top-2.5 left-2.5 z-20">
                        <span className={`px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] uppercase tracking-wider shadow-md ${slide.badgeColor}`}>
                          {slide.badge}
                        </span>
                      </div>

                      {/* Top Right Floating Price Tag */}
                      <div className="absolute top-2.5 right-2.5 z-20">
                        <span className={`px-2.5 py-0.5 rounded-lg text-xs font-black shadow-lg ${slide.priceColor}`}>
                          {slide.price}
                        </span>
                      </div>

                      {/* Bottom Gradient for Text Readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent pointer-events-none" />

                      {/* Bottom Content Card Inside Image */}
                      <div className="absolute bottom-2.5 left-2.5 right-2.5 z-20 p-2.5 rounded-xl bg-slate-950/90 backdrop-blur-md border border-slate-700/80 shadow-xl space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <h2 className="text-xs sm:text-[13px] font-black text-white leading-tight uppercase truncate">
                            {slide.title}
                          </h2>
                          <span className="text-[10px] font-bold text-amber-400 shrink-0">
                            ⏱️ {slide.duration}
                          </span>
                        </div>
                        <p className="text-[10px] sm:text-[11px] text-slate-300 leading-snug line-clamp-2">
                          {slide.desc}
                        </p>
                        <button
                          onClick={() => handleSelectSlideService(slide.serviceId)}
                          className="w-full min-h-[44px] py-2 px-3 rounded-lg bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 hover:from-amber-300 hover:to-amber-400 text-slate-950 font-black text-[11px] sm:text-xs uppercase tracking-wide flex items-center justify-center gap-1.5 shadow-md active:scale-[0.98] transition-all cursor-pointer"
                        >
                          <Scissors className="w-3.5 h-3.5 stroke-[2.5]" />
                          <span>{lang === 'nl' ? 'Kies Deze Dienst & Boek' : lang === 'tr' ? 'Bu Hizmeti Seç & Randevu Al' : 'Select This Service & Book'}</span>
                          <ArrowRight className="w-3.5 h-3.5 stroke-[2.5]" />
                        </button>
                      </div>

                    </div>
                  ))}

                  {/* Left / Right Carousel Controls */}
                  <button
                    onClick={prevSlide}
                    aria-label="Previous service"
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-slate-950/80 hover:bg-amber-500 text-white hover:text-slate-950 border border-white/20 flex items-center justify-center transition-all shadow-md cursor-pointer hover:scale-110 active:scale-95"
                  >
                    <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
                  </button>

                  <button
                    onClick={nextSlide}
                    aria-label="Next service"
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-slate-950/80 hover:bg-amber-500 text-white hover:text-slate-950 border border-white/20 flex items-center justify-center transition-all shadow-md cursor-pointer hover:scale-110 active:scale-95"
                  >
                    <ChevronRight className="w-5 h-5 stroke-[2.5]" />
                  </button>

                  {/* Dots Indicator */}
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex items-center">
                    {showcaseSlides.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveSlide(idx)}
                        aria-label={`Slide ${idx + 1}`}
                        className="w-11 h-11 min-w-[44px] min-h-[44px] flex items-center justify-center cursor-pointer"
                      >
                        <span className={`block h-1.5 rounded-full transition-all duration-300 ${
                          activeSlide === idx ? 'w-5 bg-amber-400 shadow-sm shadow-amber-400' : 'w-1.5 bg-white/60'
                        }`} />
                      </button>
                    ))}
                  </div>

                </div>

              </div>
            </div>
          </div>

        </div>

      </div>

    </section>
  );
}
